import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * CLAÈ Admin — Wipe Test Data (DANGER ZONE).
 *
 * Admin-only destructive flow. Three-step gate:
 *   1. Shows exactly what will be deleted (preview counts).
 *   2. Requires the admin's password (verified server-side).
 *   3. Requires the exact confirmation phrase.
 * Server also independently enforces all three.
 */
const CONFIRM_PHRASE =
  "I understand this will permanently delete all existing test clients and bookings.";

const COLLECTION_LABELS = {
  bookings: "Bookings / appointments",
  clients: "Client profiles",
  client_packages: "Course / package records",
  consent_forms: "Consent submissions",
  consent_refusals: "Consent refusals",
  consent_reviews: "Consent reviews",
  consultation_credits: "Consultation credits",
  credit_ledger: "Referral credit ledger",
  lip_refill_packages: "Lip refill packages",
  loyalty_cards: "Loyalty cards",
  payment_transactions: "Payment transactions",
  payments: "Payments",
  stock_history: "Stock deduction history",
  topups: "Top-up records",
};

export default function AdminWipeTestData() {
  const [preview, setPreview] = useState(null);
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("idle"); // idle | confirming
  const [auditRows, setAuditRows] = useState([]);
  const [done, setDone] = useState(null);

  const loadPreview = useCallback(async () => {
    try {
      const r = await api.get("/admin/wipe-test-data/preview");
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load wipe preview");
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const r = await api.get("/admin/wipe-test-data/audit");
      setAuditRows(r.data || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadPreview(); loadAudit(); }, [loadPreview, loadAudit]);

  const executeWipe = async () => {
    if (phrase.trim() !== CONFIRM_PHRASE) {
      toast.error("Confirmation phrase does not match exactly.");
      return;
    }
    if (!password) {
      toast.error("Enter your admin password.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/wipe-test-data", {
        password, confirm_phrase: phrase,
      });
      setDone(r.data);
      setPassword(""); setPhrase(""); setStage("idle");
      toast.success(r.data.message || "Test clients and bookings successfully removed.");
      loadPreview(); loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    }
    setBusy(false);
  };

  const total = preview?.total ?? 0;

  return (
    <div className="space-y-6" data-testid="admin-wipe-test-data">
      <div>
        <h2 className="font-serif text-2xl text-stone-900">Wipe Test Data</h2>
        <p className="text-xs text-stone-600 mt-1">
          Permanently removes all existing test clients and their test bookings. Admin accounts, treatments, templates, stock configuration, Stripe and all other business configuration are preserved.
        </p>
      </div>

      {/* Warning banner */}
      <div className="border-2 border-red-700 bg-red-50 p-5" data-testid="wipe-warning-banner">
        <p className="mono-label text-red-900 text-sm">⚠ DANGER ZONE</p>
        <p className="text-sm text-red-900 mt-2">
          <strong>WARNING:</strong> This permanently deletes test clients and bookings and cannot be undone.
        </p>
      </div>

      {done && (
        <div className="border hairline bg-emerald-50 p-4" data-testid="wipe-success-panel">
          <p className="mono-label text-emerald-800 text-xs">COMPLETED</p>
          <p className="text-sm text-emerald-900 mt-1">{done.message}</p>
          <p className="text-xs text-emerald-800 mt-2">
            Deleted {done.total_deleted} records · Model slots reset: {done.model_slots_reset} · Promo-code counters reset: {done.promo_codes_reset}
          </p>
          <p className="text-[11px] text-emerald-700 mt-1">Audit entry: {done.audit_id}</p>
        </div>
      )}

      {/* Preview counts */}
      {preview && (
        <div className="border hairline bg-white" data-testid="wipe-preview">
          <div className="px-5 py-3 border-b hairline mono-label text-[11px] text-stone-600 flex items-center justify-between">
            <span>WILL BE DELETED</span>
            <span className="tabular-nums text-stone-900">{total} records total</span>
          </div>
          <div className="divide-y hairline">
            {Object.entries(preview.counts).map(([k, v]) => (
              <div key={k} data-testid={`wipe-count-${k}`} className="px-5 py-2.5 text-sm flex items-center justify-between">
                <span className="text-stone-800">{COLLECTION_LABELS[k] || k}</span>
                <span className={`tabular-nums mono-label text-xs ${v > 0 ? "text-stone-900" : "text-stone-400"}`}>{v}</span>
              </div>
            ))}
            <div className="px-5 py-2.5 text-sm flex items-center justify-between bg-stone-50">
              <span className="text-stone-700">Model slots to reset to AVAILABLE</span>
              <span className="tabular-nums mono-label text-xs">{preview.model_slots_to_reset}</span>
            </div>
          </div>
          <div className="px-5 py-3 border-t hairline bg-stone-50 text-[11px] text-stone-600">
            Preserved: admin accounts, treatments, treatment prices &amp; configuration, consent &amp; aftercare templates, course/loyalty/referral/promo rules, consultation configuration, stock products &amp; cost, analytics, Stripe, email configuration, website content, terms &amp; privacy.
          </div>
        </div>
      )}

      {/* Confirmation gate */}
      {stage === "idle" && (
        <div>
          <button
            data-testid="wipe-begin-btn"
            onClick={() => setStage("confirming")}
            disabled={!preview || total === 0}
            className="mono-label text-xs border border-red-700 bg-red-700 text-white px-4 py-2 hover:bg-red-800 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title={total === 0 ? "Nothing to wipe — all test collections are already empty" : ""}
          >Begin wipe…</button>
        </div>
      )}

      {stage === "confirming" && (
        <div className="border-2 border-red-700 bg-white p-5 space-y-4" data-testid="wipe-confirm-form">
          <p className="text-sm text-stone-800">
            Type the confirmation phrase exactly as shown, enter your admin password, and press <strong>Wipe now</strong>.
          </p>
          <div>
            <p className="mono-label text-[11px] text-stone-600 mb-1">CONFIRMATION PHRASE</p>
            <p className="text-xs text-stone-600 mb-2 font-mono bg-stone-50 border hairline p-2 select-text">{CONFIRM_PHRASE}</p>
            <input
              data-testid="wipe-confirm-phrase"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder="Type the phrase exactly"
              className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900"
              autoComplete="off"
            />
          </div>
          <div>
            <p className="mono-label text-[11px] text-stone-600 mb-1">ADMIN PASSWORD</p>
            <input
              data-testid="wipe-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your admin password"
              className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900"
              autoComplete="current-password"
            />
            <p className="text-[11px] text-stone-500 mt-1">Verified server-side. Never stored or logged.</p>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button
              data-testid="wipe-cancel-btn"
              onClick={() => { setStage("idle"); setPassword(""); setPhrase(""); }}
              disabled={busy}
              className="mono-label text-xs border border-stone-400 text-stone-700 px-4 py-2 hover:bg-stone-100 transition"
            >Cancel</button>
            <button
              data-testid="wipe-execute-btn"
              onClick={executeWipe}
              disabled={busy || phrase.trim() !== CONFIRM_PHRASE || !password}
              className="mono-label text-xs border border-red-700 bg-red-700 text-white px-4 py-2 hover:bg-red-800 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >{busy ? "Wiping…" : "Wipe now"}</button>
          </div>
        </div>
      )}

      {/* Audit log */}
      {auditRows.length > 0 && (
        <div className="border hairline bg-white" data-testid="wipe-audit-log">
          <div className="px-5 py-3 border-b hairline mono-label text-[11px] text-stone-600">
            PREVIOUS WIPES
          </div>
          <div className="divide-y hairline">
            {auditRows.map((r) => (
              <div key={r.id} className="px-5 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-stone-900">{new Date(r.at).toLocaleString("en-GB")}</span>
                  <span className="mono-label text-[11px] text-stone-500">{r.admin_email}</span>
                </div>
                <div className="text-xs text-stone-600 mt-1">
                  Deleted {r.total_deleted} records · model slots reset: {r.model_slots_reset ?? 0} · promo counters reset: {r.promo_codes_reset ?? 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
